import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppModal from '../ui/AppModal';
import DataTable from '../ui/DataTable';
import RequirementDateRangeFilter from '../ui/RequirementDateRangeFilter';
import { getRecruiterTlRequirementDetails } from '../../services/recruiterTlRequirementDetailsService';
import { getDefaultDashboardDateRange } from '../../utils/requirementDateFilter';
import { formatDisplayDate } from '../../utils/dateFormat';
import { formatClientSummary } from '../../utils/reportFormat';
import {
  UploadActionButton,
  UploadStatusBadge,
  confirmAndToggleAssignedUserUploadStatus,
  getUploadDisplayState,
} from '../requirements/assignedUserUploadUi';
import { useLayout } from '../../layout/AppLayout';

const DEFAULT_RANGE = getDefaultDashboardDateRange();

function isInvalidDateRange(fromDate, toDate) {
  if (!fromDate || !toDate) return true;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return true;
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  return from > to;
}

function RecruiterTlRequirementDetailsSection() {
  const { tableRefresh } = useLayout();
  const [startDate, setStartDate] = useState(DEFAULT_RANGE.from);
  const [endDate, setEndDate] = useState(DEFAULT_RANGE.to);
  const [employeeId, setEmployeeId] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    startDate: DEFAULT_RANGE.from,
    endDate: DEFAULT_RANGE.to,
    employeeId: '',
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [dateError, setDateError] = useState('');
  const [report, setReport] = useState(null);
  const [detailModal, setDetailModal] = useState({
    show: false,
    title: '',
    userId: '',
    rows: [],
  });
  const [profileModal, setProfileModal] = useState({
    show: false,
    title: '',
    userId: '',
    rows: [],
  });

  const loadReport = useCallback(async (filters) => {
    const { startDate: rangeStart, endDate: rangeEnd, employeeId: rangeEmployeeId } = filters;
    if (isInvalidDateRange(rangeStart, rangeEnd)) {
      setDateError('Start Date cannot be after End Date.');
      return;
    }
    setDateError('');
    setLoading(true);
    setLoadError('');
    try {
      const response = await getRecruiterTlRequirementDetails({
        startDate: rangeStart,
        endDate: rangeEnd,
        employeeId: rangeEmployeeId || undefined,
      });
      if (response.status === 'Error') {
        setLoadError(response.msg || 'Failed to load requirement details.');
        setReport(null);
        return;
      }
      setReport(response);
    } catch (error) {
      console.error('Recruiter/TL requirement details load failed:', error);
      setLoadError(error.message || 'Failed to load requirement details.');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport(appliedFilters);
  }, [appliedFilters, loadReport, tableRefresh.candidates, tableRefresh.requirements]);

  const refreshReportAndModal = useCallback(async () => {
    const { startDate: rangeStart, endDate: rangeEnd, employeeId: rangeEmployeeId } = appliedFilters;
    if (isInvalidDateRange(rangeStart, rangeEnd)) return;

    try {
      const response = await getRecruiterTlRequirementDetails({
        startDate: rangeStart,
        endDate: rangeEnd,
        employeeId: rangeEmployeeId || undefined,
      });
      if (response.status === 'Error') return;

      setReport(response);

      setDetailModal((prev) => {
        if (!prev.show || !prev.userId) return prev;
        const employeeRow = (response.rows || []).find(
          (row) => String(row.userId) === String(prev.userId),
        );
        if (!employeeRow) return prev;
        return {
          ...prev,
          rows: employeeRow.requirements || [],
        };
      });
      setProfileModal((prev) => {
        if (!prev.show || !prev.userId) return prev;
        const employeeRow = (response.rows || []).find(
          (row) => String(row.userId) === String(prev.userId),
        );
        if (!employeeRow) return prev;
        return {
          ...prev,
          rows: employeeRow.uploadedProfiles || [],
        };
      });
    } catch (error) {
      console.error('Recruiter/TL requirement details refresh failed:', error);
    }
  }, [appliedFilters]);

  const handleSearch = () => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateError('Start Date cannot be after End Date.');
      return;
    }
    setDateError('');
    setAppliedFilters({
      startDate,
      endDate,
      employeeId,
    });
  };

  const filteredEmployeeOptions = useMemo(() => {
    const employeeOptions = report?.employeeOptions || [];
    const query = employeeSearch.trim().toLowerCase();
    if (!query) return employeeOptions;
    return employeeOptions.filter((option) => option.name.toLowerCase().includes(query));
  }, [report?.employeeOptions, employeeSearch]);

  const rows = report?.rows || [];

  const handleToggleUpload = useCallback(async (requirementRow, isManuallyActive) => {
    const requirementId = requirementRow.requirementId;
    const userId = requirementRow.assignedUserId || detailModal.userId;
    await confirmAndToggleAssignedUserUploadStatus(
      requirementId,
      userId,
      isManuallyActive,
      refreshReportAndModal,
    );
  }, [detailModal.userId, refreshReportAndModal]);

  const detailColumns = useMemo(() => [
    { key: 'requirement', label: 'Requirement', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'positions', label: 'Positions', sortable: true },
    {
      key: 'profilesSourcedCount',
      label: 'Profiles Sourced',
      sortable: true,
      render: (row) => row.profilesSourcedCount ?? 0,
    },
    { key: 'status', label: 'Status', sortable: true },
    {
      key: 'assignedDate',
      label: 'Assigned Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.assignedDate),
    },
    {
      key: 'uploadStatus',
      label: 'Upload Status',
      render: (row) => {
        const { badgeActive } = getUploadDisplayState(row);
        return (
          <div className="hr-rtl-req-upload-status-cell">
            <UploadStatusBadge isManuallyActive={badgeActive} />
          </div>
        );
      },
    },
    {
      key: 'requirementId',
      label: 'Action',
      render: (row) => {
        const { badgeActive, buttonAction, canToggle, toggleManualActive } = getUploadDisplayState(row);
        return (
          <div className="hr-rtl-req-detail-actions">
            <Link to={`/Requirments?requirementId=${encodeURIComponent(row.requirementId || '')}`}>
              Open
            </Link>
            <UploadActionButton
              isManuallyActive={badgeActive}
              buttonAction={buttonAction}
              disabled={!canToggle}
              onClick={() => {
                if (!canToggle) return;
                handleToggleUpload(row, toggleManualActive);
              }}
            />
          </div>
        );
      },
    },
  ], [handleToggleUpload]);

  const openProfileModal = (row) => {
    setProfileModal({
      show: true,
      title: `${row.name} — Profiles Sourced`,
      userId: row.userId || '',
      rows: row.uploadedProfiles || [],
    });
  };

  const profileColumns = useMemo(() => [
    { key: 'candidateName', label: 'Source Profile', sortable: true },
    { key: 'client', label: 'Client', sortable: true },
    { key: 'requirement', label: 'Requirement', sortable: true },
    { key: 'regId', label: 'Req ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true, type: 'status', useStoredLabel: true },
    {
      key: 'uploadedOn',
      label: 'Uploaded Date',
      sortable: true,
      render: (row) => formatDisplayDate(row.uploadedOn),
    },
  ], []);

  return (
    <div className="hr-rtl-req-details-page">
      <section className="hr-card hr-rtl-req-filters-card">
        <div className="hr-rtl-req-filter-header">
          <svg
            className="hr-rtl-req-filter-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M4 5h16l-6.5 7.5v5.5L10 20v-7.5L4 5z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
          </svg>
          <h2 className="hr-rtl-req-filter-title">Filters</h2>
        </div>

        <div className="hr-rtl-req-filter-toolbar">
          <div className="hr-rtl-req-filter-group">
            <span className="hr-rtl-req-filter-label">Date Range</span>
            <RequirementDateRangeFilter
              fromDate={startDate}
              toDate={endDate}
              onFromChange={setStartDate}
              onToChange={setEndDate}
              inline
              hideClearButton
              className="hr-rtl-req-date-range"
            />
          </div>

          <div className="hr-rtl-req-filter-group hr-rtl-req-filter-group-employee">
            <span className="hr-rtl-req-filter-label">Employee</span>
            <div className="hr-rtl-req-employee-fields">
              <div className="hr-rtl-req-search-wrap">
                <input
                  type="search"
                  className="form-control hr-rtl-req-filter-input hr-rtl-req-search-plain"
                  placeholder="Search employees"
                  value={employeeSearch}
                  onChange={(event) => setEmployeeSearch(event.target.value)}
                  aria-label="Search employees"
                />
              </div>
              <select
                className="form-select hr-rtl-req-filter-input hr-rtl-req-employee-select"
                value={employeeId}
                onChange={(event) => setEmployeeId(event.target.value)}
                aria-label="Employee filter"
              >
                <option value="">All Employees</option>
                {filteredEmployeeOptions.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name}
                    {employee.userType === 'TeamLead' ? ' (Team Lead)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button
            type="button"
            className="hr-rtl-req-filter-search-btn"
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </button>
        </div>

        {dateError ? <div className="alert alert-warning py-2 mb-0 mt-3">{dateError}</div> : null}
      </section>

      <section className="hr-card hr-rtl-req-results-card">
        {loadError ? <div className="alert alert-danger py-2">{loadError}</div> : null}

        {loading && !report ? (
          <p className="hr-empty-state mb-0">Loading requirement details…</p>
        ) : null}

        {!loading && report ? (
          <>
            <h3 className="hr-rtl-req-results-title">Requirement Assignment Details</h3>
            <p className="hr-page-hint mb-3">
              Eligible assignments through
              {' '}
              {formatDisplayDate(report.period?.endDate || appliedFilters.endDate)}
              {' '}
              (active requirements carry forward; profile counts use uploads in
              {' '}
              {formatDisplayDate(report.period?.startDate || appliedFilters.startDate)}
              {' → '}
              {formatDisplayDate(report.period?.endDate || appliedFilters.endDate)}
              ).
            </p>

            {rows.length > 0 ? (
              <div className="hr-rtl-req-table-wrap">
                <table className="table table-bordered table-hover table-sm align-middle hr-data-table hr-rtl-req-table">
                  <colgroup>
                    <col className="hr-rtl-req-col-name" />
                    <col className="hr-rtl-req-col-clients" />
                    <col className="hr-rtl-req-col-count" />
                    <col className="hr-rtl-req-col-count" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="hr-rtl-req-th-name">Recruiter / Team Lead</th>
                      <th className="hr-rtl-req-th-clients">Clients</th>
                      <th className="hr-rtl-req-th-count">Requirements</th>
                      <th className="hr-rtl-req-th-count">Profiles Sourced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const clientsTitle = row.clients?.length
                        ? row.clients.join(', ')
                        : 'No requirements';
                      const clientsLabel = formatClientSummary(row.clients || []);

                      return (
                        <tr key={row.userId || row.name}>
                          <td className="hr-rtl-req-td-name">
                            <button
                              type="button"
                              className="hr-ews-metric-link"
                              onClick={() => openProfileModal(row)}
                            >
                              <strong>{row.name}</strong>
                            </button>
                          </td>
                          <td className="hr-rtl-req-td-clients" title={clientsTitle}>{clientsLabel}</td>
                          <td className="hr-rtl-req-td-count">
                            {row.requirementCount > 0 ? (
                              <button
                                type="button"
                                className="hr-ews-metric-link"
                                onClick={() => setDetailModal({
                                  show: true,
                                  title: `${row.name} — Requirement Details`,
                                  userId: row.userId || '',
                                  rows: row.requirements || [],
                                })}
                              >
                                {row.requirementCount}
                              </button>
                            ) : (
                              <span>0</span>
                            )}
                          </td>
                          <td className="hr-rtl-req-td-count">
                            {row.profilesSourcedCount > 0 ? (
                              <button
                                type="button"
                                className="hr-ews-metric-link"
                                onClick={() => openProfileModal(row)}
                              >
                                {row.profilesSourcedCount}
                              </button>
                            ) : (
                              <span>0</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {rows.length === 0 && !loadError ? (
              <p className="hr-empty-state mb-0">No requirements found for the selected date range.</p>
            ) : null}
          </>
        ) : null}
      </section>

      <AppModal
        show={profileModal.show}
        onHide={() => setProfileModal({ show: false, title: '', userId: '', rows: [] })}
        title={profileModal.title}
        subtitle={`${profileModal.rows.length} profile${profileModal.rows.length === 1 ? '' : 's'} in the selected date range`}
        size="xl"
      >
        <div className="hr-rtl-req-detail-modal-table">
          <DataTable
            columns={profileColumns}
            data={profileModal.rows}
            rowKey="candidateId"
            searchPlaceholder="Search profiles"
            searchKeys={['candidateName', 'client', 'requirement', 'regId', 'status']}
            emptyMessage="No profiles uploaded by this employee in the selected date range."
            pageSize={10}
            showRowNumbers={false}
          />
        </div>
      </AppModal>

      <AppModal
        show={detailModal.show}
        onHide={() => setDetailModal({ show: false, title: '', userId: '', rows: [] })}
        title={detailModal.title}
        size="xl"
      >
        <div className="hr-rtl-req-detail-modal-table">
          <DataTable
            columns={detailColumns}
            data={detailModal.rows}
            rowKey="requirementId"
            searchPlaceholder="Search requirements"
            searchKeys={['requirement', 'client', 'status']}
            emptyMessage="No requirements for this employee in the selected period."
            pageSize={10}
            showRowNumbers={false}
          />
        </div>
      </AppModal>
    </div>
  );
}

export default RecruiterTlRequirementDetailsSection;
